// frontend/app/components/lessonPlans/teachsense/TeachSenseReflections.js
//
// The Reflect step, read back.
//
// "Anything still confusing?" is the one answer on this screen a teacher can
// act on before the next bell, so it is drawn first, in its own tinted block,
// and the students who wrote one are listed ahead of the students who did not.
// The authored prompts follow underneath in supporting type — they are context
// for the confusion, not a second thing to read at the same weight.
//
// Reflections carry evidence weight 0. Nothing here moves a mastery band, and
// nothing here is drawn as a score.

import React from "react";
import { StyleSheet, Text, View } from "react-native";

import { tint } from "../lessonPlanTheme";
import { MAX_ROWS, Section, StudentRow } from "./TeachSenseSection";
import { fill, humanizeKey } from "./teachsenseText";

// The amber the warning token resolves to; a hex because tint() blends from one.
const CONFUSION_HEX = "#f59e0b";

const confusionOf = (entry) => String(entry?.confusion || "").trim();

/**
 * Confusion first, everyone else after, each group keeping the order the server
 * sent. Array.prototype.sort is stable, so a partition is all this needs — and
 * it sorts a copy, never the caller's array.
 */
export const orderReflections = (entries) =>
  [...entries].sort(
    (a, b) => (confusionOf(b) ? 1 : 0) - (confusionOf(a) ? 1 : 0)
  );

const ReflectionRow = ({ entry, colors, isDarkMode, divided, t }) => {
  const confusion = confusionOf(entry);
  const prompts = entry?.prompts && typeof entry.prompts === "object" ? entry.prompts : {};
  const answers = Object.keys(prompts)
    .map((key) => ({ key, label: humanizeKey(key), text: String(prompts[key] ?? "").trim() }))
    .filter((answer) => answer.text);

  return (
    <StudentRow
      entry={entry}
      colors={colors}
      isDarkMode={isDarkMode}
      accent={confusion ? colors.warning : colors.textSecondary}
      hex={confusion ? CONFUSION_HEX : null}
      divided={divided}
      detail={confusion ? `${t("Still confusing")}: ${confusion}` : ""}
    >
      {confusion ? (
        <View
          style={[
            styles.confusion,
            {
              backgroundColor: tint(CONFUSION_HEX, isDarkMode, 1.4),
              borderColor: colors.teacherTipBorder,
            },
          ]}
        >
          <Text style={[styles.confusionLabel, { color: colors.warning }]}>
            {t("Still confusing")}
          </Text>
          <Text style={[styles.confusionText, { color: colors.textPrimary }]}>{confusion}</Text>
        </View>
      ) : null}
      {answers.length ? (
        answers.map((answer, index) => (
          <View key={answer.key} style={index > 0 ? styles.answerStacked : styles.answerFirst}>
            {answer.label ? (
              <Text style={[styles.answerLabel, { color: colors.textMuted }]}>{answer.label}</Text>
            ) : null}
            <Text style={[styles.answerText, { color: colors.textSecondary }]}>{answer.text}</Text>
          </View>
        ))
      ) : confusion ? null : (
        <Text style={[styles.none, { color: colors.textMuted }]}>{t("Nothing written yet")}</Text>
      )}
    </StudentRow>
  );
};

const TeachSenseReflections = ({
  reflections = null,
  colors,
  isDarkMode = false,
  isNarrow = false,
  t,
}) => {
  const translate = t || ((value) => value);
  const entries = Array.isArray(reflections) ? reflections : [];
  const ordered = orderReflections(entries);
  const shown = ordered.slice(0, MAX_ROWS);
  const overflow = ordered.length - shown.length;
  const confused = entries.filter((entry) => confusionOf(entry)).length;

  return (
    <Section
      testID="teachsense-reflections"
      colors={colors}
      isNarrow={isNarrow}
      icon="thought-bubble-outline"
      accent={colors.warning}
      title={translate("Reflections")}
      subtitle={
        confused
          ? fill(translate("{n} students flagged something still confusing."), { n: confused })
          : translate("Recorded as metacognition. Never scored.")
      }
      count={entries.length || null}
      isEmpty={entries.length === 0}
      empty={translate("No reflections have been written yet.")}
    >
      {shown.map((entry, index) => (
        <ReflectionRow
          key={entry.student_email || `reflection-${index}`}
          entry={entry}
          colors={colors}
          isDarkMode={isDarkMode}
          divided={index > 0}
          t={translate}
        />
      ))}
      {overflow > 0 ? (
        <Text style={[styles.more, { color: colors.textMuted }]}>
          {fill(translate("+{n} more students"), { n: overflow })}
        </Text>
      ) : null}
    </Section>
  );
};

const styles = StyleSheet.create({
  confusion: {
    borderRadius: 10,
    borderWidth: 1,
    paddingHorizontal: 10,
    paddingVertical: 8,
    marginTop: 6,
  },
  confusionLabel: {
    fontSize: 10,
    fontWeight: "800",
    letterSpacing: 0.6,
    textTransform: "uppercase",
    marginBottom: 3,
  },
  confusionText: { fontSize: 14, fontWeight: "600", lineHeight: 20, maxWidth: 720 },

  answerFirst: { marginTop: 8 },
  answerStacked: { marginTop: 6 },
  answerLabel: {
    fontSize: 10,
    fontWeight: "700",
    letterSpacing: 0.4,
    textTransform: "uppercase",
    lineHeight: 14,
  },
  answerText: { fontSize: 13, lineHeight: 19, maxWidth: 720 },
  none: { fontSize: 12, fontStyle: "italic", lineHeight: 17, marginTop: 4 },
  more: { fontSize: 12, fontStyle: "italic", paddingTop: 10 },
});

export default TeachSenseReflections;
